import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import notify from 'devextreme/ui/notify';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

const NO_NOTIFY_STATUSES = new Set([0, 401, 403, 404]);

// Endpoints donde el form ya muestra su propio banner de error.
// El interceptor no debe encimar un toast global ahí.
function isSilentEndpoint(url: string): boolean {
  return /\/auth\/(login|register|reset|change-password)/.test(url);
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private auth = inject(AuthService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isApiCall = req.url.startsWith('/api/') || req.url.includes('/api/');
    let r = req;

    // Convierte URLs relativas /api/... a absolutas usando environment.apiUrl
    if (isApiCall && req.url.startsWith('/api/')) {
      const absoluteUrl = req.url.replace('/api', environment.apiUrl);
      r = req.clone({ url: absoluteUrl });
    }

    // Agrega token de autorización si existe
    if (isApiCall && this.auth.accessToken) {
      r = r.clone({
        setHeaders: { Authorization: `Bearer ${this.auth.accessToken}` },
      });
    }

    return next.handle(r).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && isApiCall && !isSilentEndpoint(r.url)) {
          // Sesión expirada o token inválido. Capturamos URL actual y notificamos
          // al usuario antes de redirigir al login (vs. el logOut silencioso anterior).
          this.auth.notifySessionExpired();
          return throwError(() => err);
        }
        const shouldNotify =
          !NO_NOTIFY_STATUSES.has(err.status) && !isSilentEndpoint(r.url);
        if (shouldNotify) {
          // Nunca usar err.message: contiene el string técnico ("Http failure response for…").
          // Si el backend mandó ProblemDetails con detail/title, usamos eso; sino, fallback humano.
          const serverMsg = (err.error as any)?.detail ?? (err.error as any)?.title;
          const friendly =
            err.status === 0 ? 'No hay conexión con el servidor.' :
            err.status >= 500 ? 'El servidor tuvo un problema. Probá de nuevo en unos minutos.' :
            'Ocurrió un error procesando la solicitud.';
          notify({ message: serverMsg ?? friendly, type: 'error', displayTime: 4000, position: 'top right', width: 360 }, 'error');
        }
        return throwError(() => err);
      })
    );
  }
}
